import Cookies from 'js-cookie';
import { useMemo, useState } from 'react';
import de from 'translations/de.json';
import en from 'translations/en.json';
import fr from 'translations/fr.json';
import it from 'translations/it.json';
import nl from 'translations/nl.json';
import pl from 'translations/pl.json';

import deLocale from 'dayjs/locale/de';
// en-gb gives European date formats for English; quick fix until we implement locale-based date formats properly
import enLocale from 'dayjs/locale/en-gb';
import frLocale from 'dayjs/locale/fr';
import itLocale from 'dayjs/locale/it';
import nlLocale from 'dayjs/locale/nl';
import plLocale from 'dayjs/locale/pl';

type TranslationKey = keyof typeof en;
export type Translation = {
  [key in TranslationKey]: string;
};

export type SupportedLanguages = 'en' | 'pl' | 'nl' | 'fr' | 'de' | 'it';

export const arrayOfSupportedLanguages: Array<{ value: SupportedLanguages; label: string }> = [
  { value: 'en', label: 'English' },
  { value: 'pl', label: 'Polski' },
  { value: 'nl', label: 'Nederlands' },
  { value: 'fr', label: 'Français' },
  { value: 'de', label: 'Deutsch' },
  { value: 'it', label: 'Italiano' }
];

const translations: { [key: string]: Translation } = {
  en,
  pl: pl as unknown as Translation,
  nl: nl as unknown as Translation,
  fr: fr as unknown as Translation,
  de: de as unknown as Translation,
  it: it as unknown as Translation
};

const useTranslation = () => {
  const getLocaleForDayjs = (getLanguage: () => SupportedLanguages) => {
    switch (getLanguage()) {
      case 'pl':
        return plLocale;
      case 'nl':
        return nlLocale;
      case 'fr':
        return frLocale;
      case 'de':
        return deLocale;
      case 'it':
        return itLocale;
      default:
      case 'en':
        return enLocale;
    }
  };

  function getLanguageFromBrowser(): SupportedLanguages | null {
    const language = navigator.language.split('-')[0] as SupportedLanguages;
    if (arrayOfSupportedLanguages.some((supportedLanguage) => supportedLanguage.value === language)) return language;
    return null;
  }

  const getPreferredLanguageFromCache = () => {
    const cachedData = Cookies.get('language');
    if (cachedData) {
      return cachedData as SupportedLanguages;
    }
    return null;
  };

  const getLanguage = () => getPreferredLanguageFromCache() || getLanguageFromBrowser() || 'en';

  const [language] = useState<SupportedLanguages>(getLanguage());

  const savePreferredLanguageInCache = (value: SupportedLanguages) => {
    Cookies.set('language', value);

    // refresh page to enable translations

    location.reload();
  };

  const translation = useMemo(() => {
    return { ...translations.en, ...translations[language] };
  }, [translations, language]);

  return { translation, getLanguage, savePreferredLanguageInCache, getLocaleForDayjs };
};

export default useTranslation;
